import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TContent, TGetItemFn, TSelectContentBlock } from '@/config/types'
import { SelectLTPBlockType } from '@/enums/ltp'
import { PageName } from '@/enums/pageName'
import { useBodyOverflow } from '@/utils/domutils'

import { useAuth } from '@/auth/useAuth'
import { getParamsFromQuery } from '@/core/session/urlManagement'
import { useSession } from '@/core/session/useSession'
import {
  trackLTPPriceClick,
  trackLTPPriceSelected,
  trackPageComplete,
  trackPageView,
} from '@/events/events'

import { FastPaymentBlock } from './components/fastLTPPaymentBlock'
import { LTPPaymentModalBlock } from './components/ltpPaymentModalBlock'
import { SingleLTPInfoBlock } from './components/singleLTPInfoBlock'
import { SelectLTPResult } from './components/types'
import { TSelectLTPPageProps } from './types'

const pageName = PageName.selectLTP

function getDefaultPaymentModalIdIfQuery(blocks: TSelectContentBlock[]): string | undefined {
  const queryParams = getParamsFromQuery()
  if (!queryParams.content_price_id) {
    return
  }

  for (const block of blocks) {
    if (typeof block === 'function') {
      continue
    }
    if (block.blockType === SelectLTPBlockType.paymentModalWindow) {
      return block.modalId || block.key
    }
  }

  return
}

function getDefaultContent(contentDetails: TContent[], getItem: TGetItemFn): TContent {
  const selectedContentId = getItem(pageName)
  const selectedPlanId = getItem(PageName.selectPlan)

  let selectedContent: TContent | undefined
  if (selectedContentId) {
    selectedContent = contentDetails.find((p) => p.contentId === selectedContentId)
    if (selectedContent) {
      return selectedContent
    }
  }
  if (selectedPlanId) {
    selectedContent = contentDetails.find((d) => d.planId == selectedPlanId)
  } else {
    selectedContent = contentDetails.find((d) => !d.hasOwnProperty('planId'))
  }
  if (selectedContent) {
    return selectedContent
  }

  if (!selectedContent) {
    selectedContent = contentDetails.find((p) => p.preselected)
  }

  return selectedContent || contentDetails[0]
}

export const SelectLTPPage = ({
  getItem,
  setItem,
  pageConfig,
  setLastVisitedItem,
  config,
  paymentOptions,
}: TSelectLTPPageProps) => {
  const navigate = useNavigate()
  const sessionContext = useSession()
  const { user } = useAuth()
  const ref = useRef<HTMLDivElement>(null)

  const { pageTitle, blocks, contentDetails, skipUrl, fastPaymentSuccessUrl } = pageConfig

  const [selectedContent, setSelectedContent] = useState<TContent>(() =>
    getDefaultContent(contentDetails, getItem)
  )

  const [openedModalId, setOpenedModalId] = useState<string>(
    () => getDefaultPaymentModalIdIfQuery(blocks) || ''
  )

  useBodyOverflow(openedModalId)

  useEffect(() => {
    document.title = pageTitle

    trackPageView(
      pageName,
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )
  }, [pageTitle, sessionContext, config, user])

  const scrollToRef = useCallback(() => {
    ref?.current?.scrollIntoView()
  }, [ref])

  const selectContent = (content: TContent) => {
    setSelectedContent(content)

    trackLTPPriceClick(
      pageName,
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      selectedContent.internalPriceId,
      sessionContext,
      user
    )
  }

  const saveContent = () => {
    trackLTPPriceSelected(
      pageName,
      [selectedContent.name.toLowerCase()],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      selectedContent.internalPriceId,
      sessionContext,
      user
    )
    setItem(pageName, selectedContent.contentId)
  }

  const openModal = (modalId: string) => {
    setOpenedModalId(modalId)
    saveContent()
  }

  const closeModal = () => {
    setOpenedModalId('')
  }

  const nextPage = async (result: SelectLTPResult, modalId: string) => {
    setLastVisitedItem(pageName)
    if (result == SelectLTPResult.SKIP) {
      trackPageComplete(
        pageName,
        ['skip'],
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user
      )
      navigate(skipUrl)
      return
    }
    let stageValue
    if (result == SelectLTPResult.FAST_SUCCESS) {
      stageValue = SelectLTPResult.FAST_SUCCESS
      saveContent()
      navigate(fastPaymentSuccessUrl)
    } else {
      stageValue = SelectLTPResult.CONTINUE
      openModal(modalId)
    }
    trackPageComplete(
      pageName,
      [stageValue],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user,
      [],
      undefined,
      undefined,
      undefined,
      selectedContent.internalPriceId
    )
  }

  const scrollToAnchor = () => {
    ref?.current?.scrollIntoView()
  }

  const className = `page page-${pageName}`
  return (
    <div className={className}>
      {blocks.map((component) => {
        if (typeof component === 'function') {
          return component(scrollToRef)
        } else if (component.blockType === SelectLTPBlockType.paymentModalWindow) {
          return (
            <LTPPaymentModalBlock
              key={component.key}
              modalId={component.modalId || component.key}
              getItem={getItem}
              setItem={setItem}
              pageConfig={pageConfig}
              paymentOptions={paymentOptions}
              closeModal={closeModal}
              openedModalId={openedModalId}
            />
          )
        } else if (component.blockType === SelectLTPBlockType.fastPaymentButton) {
          return (
            <FastPaymentBlock
              key={component.key}
              ref={component.anchor ? ref : null}
              pageConfig={pageConfig}
              contents={contentDetails}
              selectedContent={selectedContent}
              selectContent={selectContent}
              onClick={(result: SelectLTPResult) => nextPage(result, component.modalId)}
              scrollToAnchor={scrollToAnchor}
              buttonName={component.buttonName}
              buttonClassName={component.buttonClassName}
              skipButtonName={component.skipButtonName}
              containerClassName={component.containerClassName}
              stripeNextActionConfig={{
                stripePromise: paymentOptions?.stripePromise,
                backendSuccessRedirectTo:
                  paymentOptions?.successRedirectTo || PageName.paymentSuccess,
              }}
            />
          )
        } else if (component.blockType === SelectLTPBlockType.singleLTPInfo) {
          return (
            <SingleLTPInfoBlock
              key={component.key}
              ref={null}
              content={selectedContent}
              className={component.className}
            />
          )
        }
      })}
    </div>
  )
}
